import {
  Card,
  CardHeader,
  CardFooter,
  Table,
  Container,
  Row,
  Col,
  Button,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import { useState } from "react";
import { useWithdrawRequestsStatus } from "lib/api-withdraw";

const WithdrawTable = ({ withdrawData, onRefresh, showAlert }) => {
  const { mutate: withdrawRequestStatusMutation } = useWithdrawRequestsStatus();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const totalPages = Math.ceil(withdrawData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = withdrawData.slice(startIndex, endIndex);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatWalletAddress = (address) => {
    if (!address) return "N/A";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      PENDING: "warning",
      COMPLETED: "success",
      REJECTED: "danger",
      PROCESSING: "info",
    };
    return (
      <Badge color={statusColors[status] || "secondary"} pill>
        {status}
      </Badge>
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleApprove = (requestId) => {
    if (requestId) {
      withdrawRequestStatusMutation({
        id: requestId,
        status: "APPROVED",
      });
      setTimeout(() => {
        onRefresh();
      }, 1000);

      // showAlert(`Withdraw request ${requestId} approved`, "success");
    }
  };

  const handleReject = (requestId) => {
    if (requestId) {
      withdrawRequestStatusMutation({
        id: requestId,
        status: "REJECTED",
      });
      setTimeout(() => {
        onRefresh();
      }, 1000);
      // showAlert(`Withdraw request ${requestId} rejected`, "success");
    }
  };

  return (
    <Container className="mt-4" fluid>
      <Row>
        <div className="col">
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">Withdraw Requests</h3>
                </Col>
                <Col xs="4" className="text-right">
                  <Button color="primary" size="sm" onClick={onRefresh}>
                    <i className="fas fa-sync-alt" /> Refresh
                  </Button>
                </Col>
              </Row>
            </CardHeader>

            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr>
                  <th scope="col">User ID</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Wallet Address</th>
                  <th scope="col">Status</th>
                  <th scope="col">Created At</th>
                  <th scope="col">Updated At</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length > 0 ? (
                  currentData.map((request, index) => (
                    <tr key={request._id || index}>
                      <td>
                        <span className="text-sm">
                          {request.userId?.slice(0, 8)}
                          ...
                        </span>
                      </td>
                      <td>
                        <span className="text-sm font-weight-bold">
                          ${request.amount?.toFixed(2) || "0.00"}
                        </span>
                      </td>
                      <td>
                        <span
                          className="text-sm"
                          id={`wallet-${index}`}
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          {formatWalletAddress(request.walletAddress)}
                        </span>
                        {request.walletAddress && (
                          <UncontrolledTooltip
                            target={`wallet-${index}`}
                            placement="top"
                          >
                            {request.walletAddress}
                          </UncontrolledTooltip>
                        )}
                      </td>
                      <td>{getStatusBadge(request.status)}</td>
                      <td>
                        <span className="text-xs">
                          {formatDate(request.createdAt)}
                        </span>
                      </td>
                      <td>
                        <span className="text-xs">
                          {formatDate(request.updatedAt)}
                        </span>
                      </td>
                      <td>
                        {request.status === "PENDING" && (
                          <div className="d-flex">
                            <Button
                              color="success"
                              size="sm"
                              className="mr-2"
                              onClick={() => handleApprove(request._id)}
                            >
                              <i className="fas fa-check" />
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleReject(request._id)}
                            >
                              <i className="fas fa-times" />
                            </Button>
                          </div>
                        )}
                        {request.status !== "PENDING" && (
                          <span className="text-muted text-sm">
                            {request.status.toLowerCase()}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="text-muted">
                        <i className="fas fa-inbox fa-2x mb-2" />
                        <p>No withdraw requests found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {withdrawData.length > 0 && (
              <CardFooter className="py-4">
                <nav aria-label="Withdraw requests pagination">
                  <ul className="pagination justify-content-end mb-0">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-angle-left" />
                        <span className="sr-only">Previous</span>
                      </button>
                    </li>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <li
                          key={page}
                          className={`page-item ${
                            page === currentPage ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        </li>
                      )
                    )}

                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <i className="fas fa-angle-right" />
                        <span className="sr-only">Next</span>
                      </button>
                    </li>
                  </ul>
                </nav>

                <div className="text-muted text-sm mt-2">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, withdrawData.length)} of{" "}
                  {withdrawData.length} entries
                </div>
              </CardFooter>
            )}
          </Card>
        </div>
      </Row>
    </Container>
  );
};

export default WithdrawTable;
